import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Handshake, Megaphone, Film, Radio, Factory, Code2, Check, ArrowRight, Sparkles, Mail, LayoutDashboard } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const PARTNER_TYPES = [
  { key: "sponsor",    icon: Handshake,  label: "Sponsor / Marque", desc: "Visibilité premium auprès de la communauté" },
  { key: "media",      icon: Radio,      label: "Média",            desc: "Radio, TV, presse, blog culturel" },
  { key: "festival",   icon: Megaphone,  label: "Festival / Event", desc: "Booker les artistes Voamada pour vos scènes" },
  { key: "label",      icon: Film,       label: "Label / Studio",   desc: "Découvrir et signer les talents émergents" },
  { key: "technology", icon: Code2,      label: "Tech / Startup",   desc: "Intégrations, APIs, distribution" },
  { key: "other",      icon: Factory,    label: "Autre",            desc: "École, ONG, institution culturelle…" },
];

const BENEFITS = [
  "Logo affiché sur la page Partenaires officielle",
  "Accès anticipé aux nouveaux artistes vérifiés",
  "Publication directe d'offres dans l'Appel à candidatures",
  "Campagnes publicitaires ciblées (musique, humour, danse…)",
  "Badge « Partenaire certifié Voamada » sur votre profil",
  "Rapports de visibilité mensuels",
];

const FEATURED_PARTNERS = [
  { name: "Votre festival ici", hint: "Réservez cet emplacement dès maintenant" },
  { name: "Votre label ici",    hint: "Soyez l'un des premiers à rejoindre" },
  { name: "Votre média ici",    hint: "Lancement public imminent" },
];

// 8 emplacements publicitaires disponibles sur la plateforme
const AD_PLACEMENTS = [
  { id: "banner",  num: 1, label: "Bannière d'accueil",      page: "Home",           impact: "Maximum",     price: "Premium",    color: "from-brand-red to-brand-orange" },
  { id: "popup",   num: 2, label: "Pop-up intro",            page: "Toutes",         impact: "Très haut",   price: "Premium",    color: "from-brand-orange to-brand-yellow" },
  { id: "explore", num: 3, label: "Carousel Explore",        page: "Explore",        impact: "Haut",        price: "Standard +", color: "from-brand-yellow to-brand-orange" },
  { id: "sidebar", num: 4, label: "Sidebar verticale",       page: "Toutes",         impact: "Récurrent",   price: "Standard",   color: "from-brand-red to-brand-yellow" },
  { id: "artist",  num: 5, label: "Profil artiste",          page: "Artistes",       impact: "Ciblé",       price: "Standard",   color: "from-brand-orange to-brand-red" },
  { id: "track",   num: 6, label: "Lecteur track",           page: "Tracks",         impact: "Contextuel",  price: "Standard",   color: "from-brand-yellow to-brand-red" },
  { id: "chat",    num: 7, label: "Chat global",             page: "Chat",           impact: "Engagement",  price: "Boost",      color: "from-brand-red to-brand-orange" },
  { id: "footer",  num: 8, label: "Pied de page",            page: "Toutes",         impact: "Branding",    price: "Découverte", color: "from-brand-orange to-brand-yellow" },
];

export const PartnersPage = () => {
  const [form, setForm] = useState({
    company: "", contact: "", email: "", phone: "", website: "",
    type: "sponsor", message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.contact || !form.email) {
      toast.error("Merci de remplir les champs obligatoires.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/partners", form);
      setSent(true);
      toast.success(data.message || "Candidature envoyée ! On vous recontacte vite.");
      setForm({ company: "", contact: "", email: "", phone: "", website: "", type: "sponsor", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.error || "Erreur lors de l'envoi.");
    } finally { setSending(false); }
  };

  return (
    <div className="px-4 md:px-12 py-10 max-w-6xl mx-auto pb-32" data-testid="partners-page">
      {/* Hero */}
      <section className="text-center mb-14 pt-4">
        <p className="inline-flex items-center gap-2 tag-pill mb-5 bg-brand-yellow/10 border-brand-yellow/40 text-brand-yellow">
          <Sparkles size={12} /> Partenaires
        </p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mb-5 text-balance">
          Construisons ensemble{" "}
          <span className="bg-gradient-to-r from-[#E31B23] via-brand-orange to-[#EAB308] bg-clip-text text-transparent">
            la scène africaine de demain
          </span>
          .
        </h1>
        <p className="text-lg md:text-xl text-white/70 max-w-3xl mx-auto text-balance">
          Marques, festivals, médias, labels : rejoignez les premiers partenaires officiels de Voamada.
        </p>
      </section>

      {/* Types de partenariat */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-16 stagger">
        {PARTNER_TYPES.map((p) => (
          <div
            key={p.key}
            className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:border-brand-orange/40 transition-colors"
            data-testid={`partner-type-${p.key}`}
          >
            <p.icon size={22} className="text-brand-orange mb-3" />
            <h3 className="font-display text-xl font-black mb-1">{p.label}</h3>
            <p className="text-sm text-white/60">{p.desc}</p>
          </div>
        ))}
      </div>

      {/* Avantages */}
      <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-brand-red/10 via-brand-orange/5 to-brand-yellow/5 p-8 md:p-12 mb-16">
        <p className="inline-flex items-center gap-2 tag-pill mb-5 bg-brand-red/10 border-brand-red/40 text-brand-red">
          🎁 Pourquoi nous rejoindre ?
        </p>
        <h2 className="font-display text-3xl md:text-4xl font-black mb-7">Les avantages partenaire.</h2>
        <ul className="grid md:grid-cols-2 gap-3">
          {BENEFITS.map((b) => (
            <li key={b} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/5">
              <span className="w-7 h-7 rounded-full bg-brand-red/15 text-brand-red flex items-center justify-center shrink-0">
                <Check size={14} />
              </span>
              <span className="text-sm">{b}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Featured placeholders */}
      <div className="mb-16">
        <h2 className="font-display text-2xl md:text-3xl font-black mb-5">Ils nous rejoignent bientôt.</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {FEATURED_PARTNERS.map((f) => (
            <div
              key={f.name}
              className="rounded-2xl border border-dashed border-white/15 bg-white/[0.02] p-8 text-center hover:border-brand-orange/40 transition-colors"
            >
              <p className="font-display text-lg font-black text-white/80 mb-2">{f.name}</p>
              <p className="text-xs text-white/50">{f.hint}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Ad Placement visual guide */}
      <div className="mb-16" data-testid="ad-placement-guide">
        <div className="flex items-center gap-2 mb-5">
          <LayoutDashboard size={18} className="text-brand-orange" />
          <h2 className="font-display text-2xl md:text-3xl font-black">
            Où apparaîtra votre publicité ?
          </h2>
        </div>
        <p className="text-white/60 max-w-2xl mb-6 text-sm">
          8 emplacements stratégiques sur la plateforme. Cliquez sur un emplacement du mockup pour voir son détail.
        </p>

        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-6">
          {/* Mockup visuel */}
          <div className="relative rounded-2xl border border-white/10 bg-gradient-to-b from-[#0f0a09] to-[#07040a] p-5 overflow-hidden">
            {/* Browser chrome */}
            <div className="flex items-center gap-1.5 mb-4">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
              <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
              <span className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
              <span className="ml-3 text-[10px] text-white/40 font-mono">voamada.com</span>
            </div>

            <div className="grid grid-cols-12 gap-2 text-[9px] sm:text-[10px] font-bold">
              {/* Header banner (1) */}
              <Slot num={1} label="BANNIÈRE D'ACCUEIL" color="from-brand-red to-brand-orange" className="col-span-12 h-16 sm:h-20" />

              {/* Popup (2) floating */}
              <div className="col-span-12 relative h-0">
                <Slot
                  num={2}
                  label="POP-UP"
                  color="from-brand-orange to-brand-yellow"
                  className="absolute -top-2 right-2 w-20 sm:w-28 h-14 sm:h-16 rounded-lg shadow-xl z-10"
                />
              </div>

              {/* Sidebar (4) + Content */}
              <Slot num={4} label="SIDEBAR" color="from-brand-red to-brand-yellow" className="col-span-3 h-40 sm:h-48" vertical />

              <div className="col-span-9 space-y-2">
                {/* Explore carousel (3) */}
                <Slot num={3} label="EXPLORE CAROUSEL" color="from-brand-yellow to-brand-orange" className="h-14 sm:h-16" />

                {/* Artist card (5) + Track (6) */}
                <div className="grid grid-cols-2 gap-2">
                  <Slot num={5} label="PROFIL ARTISTE" color="from-brand-orange to-brand-red" className="h-20 sm:h-24" />
                  <Slot num={6} label="LECTEUR TRACK"  color="from-brand-yellow to-brand-red" className="h-20 sm:h-24" />
                </div>
              </div>

              {/* Chat (7) */}
              <Slot num={7} label="CHAT GLOBAL" color="from-brand-red to-brand-orange" className="col-span-12 h-10 sm:h-12" />

              {/* Footer (8) */}
              <Slot num={8} label="PIED DE PAGE" color="from-brand-orange to-brand-yellow" className="col-span-12 h-10 sm:h-12" />
            </div>

            <p className="text-[10px] text-white/35 mt-4 text-center">
              Aperçu simplifié · Les tailles et ratios exacts sont communiqués à la signature du contrat.
            </p>
          </div>

          {/* Details list */}
          <div className="grid gap-2">
            {AD_PLACEMENTS.map((p) => (
              <div
                key={p.id}
                className="group flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/5 hover:border-brand-orange/40 transition-colors"
                data-testid={`ad-slot-info-${p.id}`}
              >
                <span className={`shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br ${p.color} text-black font-black flex items-center justify-center text-sm`}>
                  {p.num}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm truncate">{p.label}</p>
                  <p className="text-[11px] text-white/50 truncate">
                    Page : {p.page} · Impact : {p.impact}
                  </p>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-brand-yellow bg-brand-yellow/10 border border-brand-yellow/30 rounded-full px-2 py-0.5 shrink-0">
                  {p.price}
                </span>
              </div>
            ))}
            <p className="text-[11px] text-white/40 mt-3 leading-relaxed">
              💡 Mentionnez l'<span className="text-brand-orange">emplacement souhaité</span> dans votre message ci-dessous —
              on vous transmet le kit tarifaire complet sous 48h.
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div
        className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-10"
        data-testid="partners-form-block"
      >
        <div className="flex items-center gap-3 mb-6">
          <Mail size={20} className="text-brand-orange" />
          <h2 className="font-display text-2xl md:text-3xl font-black">Candidature partenaire</h2>
        </div>

        {sent ? (
          <div className="py-10 text-center" data-testid="partners-thank-you">
            <div className="inline-flex w-14 h-14 rounded-full bg-brand-red/15 text-brand-red items-center justify-center mb-4">
              <Check size={28} />
            </div>
            <h3 className="font-display text-2xl font-black mb-2">Merci !</h3>
            <p className="text-white/70">Votre candidature a bien été reçue. L'équipe Voamada vous recontacte sous 48h.</p>
            <Link to="/explore" className="btn-ghost mt-6 inline-flex items-center gap-2">
              Explorer les talents <ArrowRight size={14} />
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="grid md:grid-cols-2 gap-4" data-testid="partners-form">
            <Field label="Entreprise / Organisation *" testid="partners-field-company">
              <input required value={form.company} onChange={set("company")} className={inputCls} />
            </Field>
            <Field label="Contact (nom complet) *" testid="partners-field-contact">
              <input required value={form.contact} onChange={set("contact")} className={inputCls} />
            </Field>
            <Field label="Email *" testid="partners-field-email">
              <input required type="email" value={form.email} onChange={set("email")} className={inputCls} />
            </Field>
            <Field label="Téléphone" testid="partners-field-phone">
              <input value={form.phone} onChange={set("phone")} className={inputCls} />
            </Field>
            <Field label="Site web / Réseaux" testid="partners-field-website">
              <input value={form.website} onChange={set("website")} placeholder="https://…" className={inputCls} />
            </Field>
            <Field label="Type de partenariat *" testid="partners-field-type">
              <select required value={form.type} onChange={set("type")} className={inputCls}>
                {PARTNER_TYPES.map((p) => (<option key={p.key} value={p.key}>{p.label}</option>))}
              </select>
            </Field>
            <Field label="Votre message" full testid="partners-field-message">
              <textarea rows={4} value={form.message} onChange={set("message")} className={inputCls} placeholder="Parlez-nous de votre projet, vos attentes, votre audience…" />
            </Field>
            <div className="md:col-span-2 flex flex-wrap gap-3 items-center">
              <button
                type="submit"
                disabled={sending}
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
                data-testid="partners-submit-btn"
              >
                {sending ? "Envoi…" : <>Envoyer ma candidature <ArrowRight size={14} /></>}
              </button>
              <p className="text-xs text-white/40">* Champs obligatoires · Vos données restent confidentielles.</p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const inputCls = "w-full rounded-xl bg-white/[0.03] border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-brand-orange/60 transition-colors";

const Field = ({ label, children, full, testid }) => (
  <div className={full ? "md:col-span-2" : ""} data-testid={testid}>
    <label className="block text-xs uppercase tracking-widest text-white/50 mb-2">{label}</label>
    {children}
  </div>
);

const Slot = ({ num, label, color, className = "", vertical = false }) => (
  <div
    className={`relative rounded-lg bg-gradient-to-br ${color} overflow-hidden border border-white/10 group hover:scale-[1.02] transition-transform cursor-help ${className}`}
    title={label}
    data-testid={`ad-mockup-slot-${num}`}
  >
    <div className="absolute inset-0 bg-black/20" />
    <div className={`absolute inset-0 flex ${vertical ? "flex-col" : "flex-row"} items-center justify-center gap-1 px-2 text-center`}>
      <span className="bg-black/60 text-white text-[10px] font-black rounded-full w-5 h-5 flex items-center justify-center shrink-0">
        {num}
      </span>
      <span className="text-white font-black tracking-wider drop-shadow leading-tight">{label}</span>
    </div>
  </div>
);

export default PartnersPage;
